import * as React from 'react';
import * as NEA from 'fp-ts/NonEmptyArray';
import * as R from 'fp-ts/Record';
import * as A from 'fp-ts/Array';
import * as O from 'fp-ts/Option';

import { pipe } from 'fp-ts/lib/function';
import { VoteFragmentFragment } from '../../generated/graphql';
import { RadioChoice } from '../../components/RadioChoice';
import { byVoteType } from '../../utils/sorting';
import { getEqNode } from '../../utils/comparison';
import { shuffleVotes } from './shuffleVotes';
import { VoteCarousel } from './VoteCarousel';
import { getVoteLabel } from './voteLabelLookup';

export interface Props {
  votes: VoteFragmentFragment[];
}

export const ViewVotes = ({ votes }: Props) => {
  const groupedVotes = React.useMemo(
    () =>
      pipe(
        votes,
        shuffleVotes,
        NEA.groupBy((v) => v.voteType.id),
      ),
    [votes],
  );

  const availableVoteTypes = pipe(
    votes,
    A.map((v) => v.voteType),
    A.uniq(getEqNode()),
    A.sort(byVoteType),
  );

  const [selectedVoteTypeO, setVoteTypeO] = React.useState(() =>
    pipe(
      availableVoteTypes,
      A.head,
      O.map((vt) => vt.id),
    ),
  );

  const selectedVoteType = pipe(
    selectedVoteTypeO,
    O.chain((selectedId) =>
      pipe(
        availableVoteTypes,
        A.findFirst((vt) => vt.id === selectedId),
      ),
    ),
  );

  const selectedVotes = pipe(
    selectedVoteTypeO,
    O.chain((vt) => R.lookup(vt, groupedVotes)),
    O.getOrElse<VoteFragmentFragment[]>(() => []),
  );

  return pipe(
    selectedVoteType,
    O.map((selectedVoteType) => (
      <div className="space-y-6" key="1">
        {availableVoteTypes.length > 1 ? (
          <RadioChoice.Container
            value={selectedVoteType}
            onChange={({ id }) => pipe(id, O.some, setVoteTypeO)}
          >
            {availableVoteTypes.map((voteType) => (
              <RadioChoice.Option key={voteType.id} value={voteType}>
                {getVoteLabel(voteType.name)} {voteType.emoji}
              </RadioChoice.Option>
            ))}
          </RadioChoice.Container>
        ) : (
          <h2 className="text-xl font-semibold">
            {getVoteLabel(selectedVoteType.name)} {selectedVoteType.emoji}
          </h2>
        )}

        {A.isEmpty(selectedVotes) ? (
          <p>No votes here yet...</p>
        ) : (
          <VoteCarousel key={selectedVoteType.id} votes={selectedVotes} />
        )}
      </div>
    )),
    O.getOrElse(() => <p>There was a problem displaying the votes</p>),
  );
};
